<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  selectedValues: {
    type: [String, Number, Array],
    default: () => [],
  },
  floating: {
    type: Boolean,
    default: false,
  },
  floatingClass: {
    type: [String, Object, Array],
    default: '',
  },
  floatingStyle: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['select', 'search']);

const { t } = useI18n();

const searchValue = defineModel('searchValue', {
  type: String,
  default: '',
});

const searchInput = ref(null);
const dropdownElement = ref(null);

const isSelected = option => {
  if (Array.isArray(props.selectedValues)) {
    return props.selectedValues.includes(option.value);
  }
  return option.value === props.selectedValues;
};

// Opções com `groupLabel` ganham um título de seção quando o grupo muda.
const showGroupLabel = (option, index) =>
  Boolean(option.groupLabel) &&
  option.groupLabel !== props.options[index - 1]?.groupLabel;

const onInputSearch = event => {
  searchValue.value = event.target.value;
  emit('search', event.target.value);
};

defineExpose({
  focus: () => searchInput.value?.focus(),
  element: dropdownElement,
});
</script>

<template>
  <div
    v-show="open"
    ref="dropdownElement"
    class="transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
    :class="floating ? floatingClass : 'absolute z-50 mt-1 w-full'"
    :style="floating ? floatingStyle : undefined"
    data-combobox-dropdown
  >
    <div class="relative border-b border-n-strong">
      <Spinner
        v-if="loading"
        :size="16"
        class="absolute top-2.5 start-3 text-n-slate-11"
      />
      <Icon
        v-else
        icon="i-lucide-search"
        class="absolute top-2.5 size-4 start-3"
      />
      <input
        ref="searchInput"
        :value="searchValue"
        type="search"
        :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
        class="reset-base w-full py-2 !ps-10 !pe-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
        @input="onInputSearch"
      />
    </div>
    <ul
      class="py-1 mb-0 overflow-auto max-h-60"
      role="listbox"
      :aria-multiselectable="multiple"
    >
      <template
        v-for="(option, index) in options"
        :key="`${option.value}-${index}`"
      >
        <li
          v-if="showGroupLabel(option, index)"
          role="presentation"
          class="px-3 pt-2 pb-1 text-xs font-medium text-n-slate-11"
          :class="{ 'mt-1 border-t border-n-weak': index > 0 }"
        >
          {{ option.groupLabel }}
        </li>
        <li
          class="relative flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150"
          :class="{
            'bg-n-alpha-2': isSelected(option),
            'cursor-pointer hover:bg-n-alpha-2': !option.disabled,
            'cursor-not-allowed opacity-60': option.disabled,
          }"
          role="option"
          :aria-selected="isSelected(option)"
          :aria-disabled="option.disabled || undefined"
          @click.stop="!option.disabled && emit('select', option)"
        >
          <slot name="option" :option="option" :selected="isSelected(option)">
            <span
              :class="{
                'font-medium': isSelected(option),
              }"
              class="flex min-w-0 items-center gap-2 text-n-slate-12"
            >
              <Icon
                v-if="option.icon"
                :icon="option.icon"
                class="size-4 flex-shrink-0"
                :class="option.iconClass || 'text-n-slate-11'"
                :title="option.iconLabel || undefined"
              />
              <span v-if="option.iconLabel" class="sr-only">
                {{ option.iconLabel }}
              </span>
              <span class="min-w-0 break-words">{{ option.label }}</span>
            </span>
          </slot>
          <span
            v-if="isSelected(option)"
            class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
          />
        </li>
      </template>
      <li v-if="options.length === 0" class="px-3 py-2 text-sm text-n-slate-11">
        {{ emptyState || t('COMBOBOX.EMPTY_STATE') }}
      </li>
    </ul>
  </div>
</template>
